import { Auth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { SupabaseClient, createClient } from "@supabase/supabase-js";
import { Database } from "@/lib/supabase-types";
import { EmployerSignupFields, EmployerFormData } from "@/components/auth/EmployerSignupFields";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { User, Briefcase } from "lucide-react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
interface SignupFormProps {
  onError?: (error: Error) => void;
  onComplete?: (userData: any) => void;
  selectedRole?: "candidate" | "employer" | null;
  enforceEmployerRole?: boolean;
  initialEmployerData?: EmployerFormData;
  onEmployerDataChange?: (data: Partial<EmployerFormData>) => void;
  collectEmployerData?: boolean;
  onGoogleSignup?: () => void;
  onLinkedInSignup?: () => void;
  isLoading?: boolean;
}
interface UserMetadata {
  role: "candidate" | "employer";
  full_name?: string;
  organization?: string;
  company_size?: string;
  industry?: string;
  company_description?: string;
}
const signupFormSchema = z.object({
  email: z.string().email({
    message: "Please enter a valid email address"
  }),
  password: z.string().min(6, {
    message: "Password must be at least 6 characters"
  }),
  full_name: z.string().min(1, {
    message: "Full name is required"
  }),
  organization: z.string().optional()
});
type SignupFormValues = z.infer<typeof signupFormSchema>;
export const SignupForm = ({
  onError,
  onComplete,
  selectedRole: initialSelectedRole,
  enforceEmployerRole = false,
  initialEmployerData,
  onEmployerDataChange,
  collectEmployerData = false,
  onGoogleSignup,
  onLinkedInSignup,
  isLoading: externalIsLoading = false
}: SignupFormProps) => {
  const {
    toast
  } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const [selectedRole, setSelectedRole] = useState<"candidate" | "employer" | null>(enforceEmployerRole ? 'employer' : initialSelectedRole || 'employer');
  const [employerData, setEmployerData] = useState<EmployerFormData>(initialEmployerData || {
    organization: "",
    companySize: "1-10",
    industry: "",
    companyDescription: ""
  });
  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupFormSchema),
    defaultValues: {
      email: "",
      password: "",
      full_name: "",
      organization: ""
    },
    mode: "onBlur"
  });
  useEffect(() => {
    const currentSchema = form.getValues();
    if (selectedRole === 'employer') {
      form.resetField('organization', {
        defaultValue: currentSchema.organization || employerData.organization || ""
      });
    }
  }, [selectedRole, form, employerData.organization]);
  useEffect(() => {
    if (initialEmployerData) {
      setEmployerData(initialEmployerData);
    }
  }, [initialEmployerData]);
  useEffect(() => {
    if (enforceEmployerRole) {
      setSelectedRole('employer');
    } else if (initialSelectedRole) {
      setSelectedRole(initialSelectedRole);
    }
  }, [initialSelectedRole, enforceEmployerRole]);
  const handleEmployerDataChange = (data: Partial<EmployerFormData>) => {
    const updatedData = {
      ...employerData,
      ...data
    };
    setEmployerData(updatedData);
    if (onEmployerDataChange) {
      onEmployerDataChange(data);
    }
  };
  const [showEmployerFields, setShowEmployerFields] = useState(false);
  useEffect(() => {
    setShowEmployerFields(false);
  }, [selectedRole, enforceEmployerRole, collectEmployerData]);
  const handleRoleChange = (role: "candidate" | "employer") => {
    setSelectedRole(role);
  };
  const userRole = enforceEmployerRole ? 'employer' : selectedRole;
  const finalEmployerData = initialEmployerData || employerData;
  const prepareUserMetadata = (formValues: SignupFormValues): UserMetadata => {
    if (userRole === 'employer') {
      return {
        role: userRole,
        full_name: formValues.full_name,
        organization: formValues.organization || finalEmployerData.organization,
        company_size: finalEmployerData.companySize,
        industry: finalEmployerData.industry,
        company_description: finalEmployerData.companyDescription
      };
    } else {
      return {
        role: userRole || 'candidate',
        full_name: formValues.full_name
      };
    }
  };
  const handleSubmit = async (formValues: SignupFormValues) => {
    if (!userRole) {
      toast({
        title: "Role Required",
        description: "Please select a role: Candidate or Employer",
        variant: "destructive",
      });
      return;
    }
    if (userRole === 'employer' && !formValues.organization) {
      toast({
        title: "Company Name Required",
        description: "Company name is required for employer accounts",
        variant: "destructive",
      });
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      console.log("Starting signup process for:", formValues.email, "with role:", userRole);
      const signupMetadata = prepareUserMetadata(formValues);
      console.log("[SignupForm] Prepared signup metadata:", signupMetadata);
      const {
        data,
        error: signUpError
      } = await supabase.auth.signUp({
        email: formValues.email,
        password: formValues.password,
        options: {
          data: signupMetadata
        }
      });
      if (signUpError) throw signUpError;
      console.log("Signup successful, data:", data);
      if (data.user) {
        toast({
          title: "Verification Required",
          description: "A verification code has been sent to your email."
        });
        const isCreatingTest = sessionStorage.getItem('isCreatingTest') === 'true';
        if (isCreatingTest) {
          console.log("[SignupForm] Test creation flow detected, using DialogOtpForm");
          if (onComplete) {
            onComplete({
              email: formValues.email,
              ...data
            });
          }
          return;
        }
        const returnToFromState = location.state?.returnTo;
        const searchParams = new URLSearchParams(location.search);
        const returnToFromParams = searchParams.get('returnTo');
        const returnTo = returnToFromState || returnToFromParams;
        navigate('/verify-otp', {
          state: {
            email: formValues.email,
            type: 'signup',
            returnTo: returnTo
          }
        });
        if (onComplete) {
          onComplete({
            id: data.user.id,
            email: data.user.email,
            full_name: formValues.full_name,
            role: userRole,
            ...(userRole === 'employer' ? {
              organization: formValues.organization || finalEmployerData.organization,
              ...finalEmployerData
            } : {})
          });
        }
      }
    } catch (err: any) {
      console.error("Signup error:", err);
      setError(err.message || "Failed to create account. Please try again.");
      // Remove toast from here to avoid duplicate - let parent component handle it
      if (onError) {
        onError(err);
      }
    } finally {
      setIsLoading(false);
    }
  };
  const shouldShowRoleSelection = !enforceEmployerRole && !initialSelectedRole && !collectEmployerData;
  
  // Set employer as default when role selection is shown
  useEffect(() => {
    if (shouldShowRoleSelection && selectedRole === null) {
      setSelectedRole('employer');
    }
  }, [shouldShowRoleSelection, selectedRole]);

  return <div className="w-full max-w-lg">
      {/* Logo */}
      <div className="flex justify-center mb-6">
        <div className="flex items-center space-x-2">
          <img 
            src="/lovable-uploads/e7e0cd62-757f-4e57-b3bf-42cffdc8d843.png" 
            alt="Testera Logo" 
            className="h-7 w-7"
          />
          <span className="text-xl font-bold text-primary tracking-tight">
            Testera
          </span>
        </div>
      </div>
      
      <div className="text-center mb-4">
        <h1 className="text-2xl font-bold text-foreground mb-2">Create Your Free Account</h1>
        <p className="text-sm text-muted-foreground">100% Free. No credit card needed.</p>
      </div>

      {shouldShowRoleSelection && <div className="mb-4">
          <h2 className="text-base font-medium text-foreground mb-2">Sign up as</h2>
          <Tabs value={selectedRole || ""} className="w-full" onValueChange={value => handleRoleChange(value as "candidate" | "employer")}>
            <TabsList className="grid w-full grid-cols-2 mb-2 bg-transparent border-0 p-0 gap-2">
              <TabsTrigger value="employer" className="flex items-center justify-center gap-2 h-10 px-3 border border-border bg-background data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary rounded-md">
                <Briefcase className="h-4 w-4" />
                <span>Employer</span>
              </TabsTrigger>
              <TabsTrigger value="candidate" className="flex items-center justify-center gap-2 h-10 px-3 border border-border bg-background data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary rounded-md">
                <User className="h-4 w-4" />
                <span>Candidate</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>}

      {showEmployerFields && <div className="mb-4 space-y-3 border border-border rounded-sm p-3 bg-card/50">
          <h3 className="font-medium text-foreground text-sm">Employer Information</h3>
          
          <EmployerSignupFields data={employerData} onChange={handleEmployerDataChange} />
        </div>}
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-3">
          <FormField control={form.control} name="full_name" render={({
          field
        }) => <FormItem className="space-y-1">
                <FormLabel className="text-foreground text-sm">Full Name</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="Your full name" className="text-card-foreground h-9 text-sm" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
          
          {userRole === 'employer' && <FormField control={form.control} name="organization" render={({
          field
        }) => <FormItem className="space-y-1">
                  <FormLabel className="text-foreground text-sm">Company Name</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="Your company name" className="text-card-foreground h-9 text-sm" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />}
          
          <FormField control={form.control} name="email" render={({
          field
        }) => <FormItem className="space-y-1">
                <FormLabel className="text-foreground text-sm">Email address</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="Your email address" className="text-card-foreground h-9 text-sm" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
          
          <FormField control={form.control} name="password" render={({
          field
        }) => <FormItem className="space-y-1">
                <FormLabel className="text-foreground text-sm">Password</FormLabel>
                <FormControl>
                  <Input type="password" placeholder="Password (min. 6 characters)" className="text-card-foreground h-9 text-sm" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
          
          <div className="text-xs text-muted-foreground mt-2">
            By signing up, you agree to our{" "}
            <Link to="/privacy-policy" className="text-secondary hover:underline">
              Privacy Policy
            </Link>{" "}
            and{" "}
            <Link to="/terms-and-conditions" className="text-secondary hover:underline">
              Terms & Conditions
            </Link>
          </div>
          
          <Button type="submit" disabled={isLoading} className="w-full text-white bg-[#e16702] h-10 text-sm relative overflow-hidden before:content-[''] before:absolute before:top-0 before:left-0 before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/30 before:to-transparent before:animate-gloss">
            {isLoading ? "Signing up..." : (userRole === 'candidate' ? "GET STARTED" : "START HIRING FREE")}
          </Button>
        </form>
      </Form>
      
      {selectedRole && <div className="mt-3 text-center">
          <p className="text-xs text-muted-foreground">
            Signing up as: 
            <Badge variant="outline" className="ml-1 font-medium text-xs">
              {selectedRole === 'employer' ? 'Employer' : 'Candidate'}
            </Badge>
          </p>
        </div>}

      {(onGoogleSignup || onLinkedInSignup) && (
        <>
          <div className="relative my-4">
            <Separator />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="bg-background text-muted-foreground px-2 text-xs">OR</span>
            </div>
          </div>

          <div className="space-y-2">
            {onGoogleSignup && (
              <Button 
                type="button" 
                variant="outline" 
                className="w-full flex items-center justify-center gap-2 border-border hover:bg-accent h-9 text-sm" 
                onClick={onGoogleSignup} 
                disabled={externalIsLoading}
              >
                <svg width="16" height="16" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                  <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
                  <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
                  <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
                  <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
                </svg>
                Sign up with Google
              </Button>
            )}

            {onLinkedInSignup && (
              <Button 
                type="button" 
                variant="outline" 
                className="w-full flex items-center justify-center gap-2 border-border hover:bg-accent h-9 text-sm" 
                onClick={onLinkedInSignup} 
                disabled={externalIsLoading}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="#0077B5"/>
                </svg>
                Sign up with LinkedIn
              </Button>
            )}
          </div>

          <div className="text-center mt-3">
            <p className="text-muted-foreground text-xs">
              Already have an account?{" "}
              <Link to={`/login${window.location.search}`} className="text-primary hover:text-primary/90 text-xs">
                Sign in
              </Link>
            </p>
          </div>
        </>
      )}
    </div>;
};